<template>
  <div
    v-if="visibleSearchModels.length"
    :class="inHeader ? 'header-quick-search' : 'sidebar-quick-search'"
  >
    <quick-search
      :models="visibleSearchModels"
      :current-id="currentProformId"
      :current-model="proformModel"
      :placeholder="$t('quickSearch.placeholder')"
      fixed-panel
      :dark="!inHeader"
      @select="goTo"
    />
  </div>
</template>

<script>
import { wencesApi, getCurrentClientDbName } from "@/api/wencesApi";
import QuickSearch from "@/components/QuickSearch.vue";
import {
  dbNameInTenantList,
  dbNameMatchesTenant,
} from "@/utils/dbNameMatchesTenant";
import { FOBTRADING_MODULE_NAME } from "@/modules/fobtrading_module";

/**
 * Qué se busca desde el menú, en orden de aparición. Los nombres de modelo
 * cambian por tenant: se matchea contra sus ModelDefinition y se saltea lo que
 * no exista, así el buscador sirve igual en cualquier base. `hiddenIn` saca el
 * modelo en tenants donde existe la definición pero no se usa.
 */
const SEARCH_KINDS = [
  {
    key: "proform",
    candidates: ["proform"],
    fallbackLabel: "Proform",
    // Alisof opera con contratos: aunque tenga la definición, no usa proformas.
    hiddenIn: ["alisof"],
  },
  {
    key: "shipping",
    candidates: ["shipping"],
    fallbackLabel: "Shipping",
    // Se pide de más para poder saltar a la operación sin otra consulta.
    fields: "custom_proform",
    hiddenIn: ["alisof"],
  },
  {
    key: "contrato",
    candidates: ["contrato", "contract"],
    fallbackLabel: "Contracts",
  },
  { key: "contact", candidates: ["contact"], fallbackLabel: "Contacts" },
  {
    key: "bankaccount",
    candidates: ["bankaccount", "bank_account", "bank"],
    fallbackLabel: "Bank Accounts",
  },
  { key: "product", candidates: ["product"], fallbackLabel: "Products" },
];

export default {
  name: "SidebarQuickSearch",
  components: {
    QuickSearch,
  },
  inject: { dashboardLayout: { default: null } },
  props: {
    /** Módulos activos del tenant: definen a dónde navega el resultado elegido. */
    activeModules: { type: Array, default: () => [] },
    /** En el encabezado del teléfono: paleta clara y el desplegable a todo el ancho. */
    inHeader: { type: Boolean, default: false },
  },
  data() {
    return {
      searchModels: [],
      proformModel: "",
      shippingModel: "",
    };
  },
  computed: {
    /** Solo los modelos que el rol puede ver, con el mismo permiso que el menú. */
    visibleSearchModels() {
      const layout = this.dashboardLayout;
      if (!layout) return this.searchModels;
      return this.searchModels.filter((m) =>
        layout.hasPermission(`${m.name.toLowerCase()}.view`)
      );
    },
    /**
     * El prop llega vacío hasta que el layout resuelve los módulos, y un click
     * temprano no debe caer en la ficha del modelo: se completa con la copia de
     * localStorage, la misma que usa el guard de ruta.
     */
    resolvedModules() {
      const fromProp = Array.isArray(this.activeModules)
        ? this.activeModules
        : [];
      if (fromProp.length) return fromProp;
      try {
        const stored = JSON.parse(localStorage.getItem("activeModules"));
        return Array.isArray(stored) ? stored : [];
      } catch (error) {
        return [];
      }
    },
    isFobtradingModuleActive() {
      return this.resolvedModules.some((module) => {
        const name =
          typeof module === "string" ? module : module && module.name;
        return name === FOBTRADING_MODULE_NAME;
      });
    },
    /**
     * Solo fobtrading manda proforma y shipping a la vista Operación. El módulo
     * FOB también está activo en otros tenants (duntower), que siguen abriendo
     * la ficha del modelo; y sin el módulo el guard bloquearía la ruta.
     */
    goesToFobOperation() {
      if (!this.isFobtradingModuleActive) return false;
      return dbNameMatchesTenant(getCurrentClientDbName(), "fobtrading");
    },
    /** Id de la operación abierta, para marcarla en la lista de resultados. */
    currentProformId() {
      const id =
        this.$route && this.$route.params ? this.$route.params.proformId : "";
      return id ? String(id) : "";
    },
  },
  created() {
    this.resolveSearchModels();
  },
  methods: {
    async resolveSearchModels() {
      try {
        const { data } = await wencesApi.get(
          "/api/gotrading/model-definitions"
        );
        const definitions = Array.isArray(data) ? data : [];
        const models = [];
        const dbName = getCurrentClientDbName();

        SEARCH_KINDS.forEach((kind) => {
          if (dbNameInTenantList(dbName, kind.hiddenIn)) return;
          const found = definitions.find(
            (definition) =>
              definition &&
              definition.modelName &&
              kind.candidates.includes(
                String(definition.modelName).toLowerCase()
              )
          );
          if (!found) return;
          models.push({
            name: found.modelName,
            label: this.$t(found.label || kind.fallbackLabel),
            fields: kind.fields,
          });
          if (kind.key === "proform") this.proformModel = found.modelName;
          if (kind.key === "shipping") this.shippingModel = found.modelName;
        });

        this.searchModels = models;
      } catch (error) {
        console.error("[sidebar] quick search:", error);
        this.searchModels = [];
      }
    },
    /** Un reference puede venir como id suelto o ya poblado. */
    extractId(value) {
      if (!value) return "";
      if (typeof value === "object") {
        return value._id ? String(value._id) : "";
      }
      return String(value);
    },
    /**
     * custom_proform en el shipping, releído del registro si el listado no lo
     * trajo. Devuelve "" y deja que se intente por el otro lado.
     */
    async fetchShippingProformId(shippingId) {
      if (!this.shippingModel) return "";
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${encodeURIComponent(
            this.shippingModel
          )}/${encodeURIComponent(shippingId)}`
        );
        const record = (data && data.data) || data;
        return this.extractId(record && record.custom_proform);
      } catch (error) {
        console.error("[sidebar] shipping por id:", error);
        return "";
      }
    },
    /**
     * El shipping no guarda siempre la proforma en custom_proform: cuando falta,
     * se relee el registro y, si sigue vacío, se busca la proforma que lo tenga
     * en su array de shippings.
     */
    async findProformOfShipping(shippingId, row) {
      const direct = this.extractId(row && row.custom_proform);
      if (direct) return direct;
      const fromRecord = await this.fetchShippingProformId(shippingId);
      if (fromRecord) return fromRecord;
      if (!this.proformModel) return "";
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${encodeURIComponent(
            this.proformModel
          )}?shippings=${encodeURIComponent(
            shippingId
          )}&perPage=1&page=1&fields=_id`
        );
        const rows = Array.isArray(data) ? data : (data && data.items) || [];
        return rows.length && rows[0]._id ? String(rows[0]._id) : "";
      } catch (error) {
        console.error("[sidebar] proforma del shipping:", error);
        return "";
      }
    },
    goToOperation(proformId) {
      if (proformId === this.currentProformId) return;
      this.$router.push({
        name: "FobtradingOperacion",
        params: { proformId },
      });
    },
    openDynamicModel(modelName, recordId) {
      this.$router.push(
        `/dynamic-model/${modelName}/view/${encodeURIComponent(recordId)}`
      );
    },
    async goTo(id, modelName, row) {
      const recordId = id ? String(id) : "";
      if (!recordId || !modelName) return;

      // En fobtrading, proforma y shipping siempre abren /operacion/<proformId>.
      // El resto de los tenants abre la ficha del modelo como siempre.
      if (this.goesToFobOperation) {
        if (modelName === this.proformModel) {
          this.goToOperation(recordId);
          return;
        }
        if (modelName === this.shippingModel) {
          const proformId = await this.findProformOfShipping(recordId, row);
          if (proformId) {
            this.goToOperation(proformId);
            return;
          }
          // Shipping huérfano: no hay operación a la que llevarlo.
        }
      }

      this.openDynamicModel(modelName, recordId);
    },
  },
};
</script>

<style scoped>
.sidebar-quick-search {
  padding: 0 15px 10px;
}

/*
 * Con el menú contraído no se muestra nada: solo cuando se abre, sea por hover
 * en desktop (body.sidebar-expanded) o por el toggle en mobile (.nav-open).
 */
/* Teléfono: el desplegable ocupa el ancho de la pantalla, no solo el del input. */
.header-quick-search ::v-deep .fob-op-search-panel {
  left: 0.5rem !important;
  width: calc(100vw - 1rem) !important;
  max-height: 60vh;
}
.header-quick-search ::v-deep .input-group {
  flex-wrap: nowrap;
}
.header-quick-search ::v-deep .form-control {
  font-size: 1rem; /* menos de 16px y iOS hace zoom al enfocar */
}
/* Campo en forma de píldora, como el buscador de cualquier app. */
.header-quick-search ::v-deep .input-group > :first-child {
  border-radius: 999px 0 0 999px;
  border-right: 0;
}
.header-quick-search ::v-deep .input-group > :last-child {
  border-radius: 0 999px 999px 0 !important;
}
.header-quick-search ::v-deep .input-group > .form-control:not(:last-child) {
  border-right: 0;
}
.header-quick-search ::v-deep .input-group .btn-outline-secondary {
  border-color: var(--bs-border-color);
  border-left: 0;
  color: var(--bs-secondary-color);
}
.header-quick-search ::v-deep .input-group .btn-outline-secondary:hover {
  background: transparent;
}

body.sidebar-mini .sidebar-quick-search {
  display: none;
}

body.sidebar-mini.sidebar-expanded .sidebar-quick-search,
body.sidebar-mini .nav-open .sidebar-quick-search {
  display: block;
}
</style>
